import type { FC } from 'react'

import { ImageWithIconFallback } from '@skytech/ui/Image'
import CheckboxBlankCircleOutlineIcon from 'mdi-react/CheckboxBlankCircleOutlineIcon'
import CheckboxMarkedCircleOutlineIcon from 'mdi-react/CheckboxMarkedCircleOutlineIcon'
import ImageOffOutlineIcon from 'mdi-react/ImageOffOutlineIcon'
import { twJoin } from 'tailwind-merge'

import { parseInStock } from '@/components/ecom-policy/policy/helpers'
import { FormattedNumberWithCurrency } from '@/components/FormattedNumberWithCurrency'
import { Translation } from '@/components/i18n/Translation'
import { useIsThreeStepIT } from '@/hooks/user'

export const Asset: FC<{
  id: number
  name: string
  alias: string | null
  imgUrl: string | null
  inStock?: string
  price: number
  selectedProductId?: number
  currency: string | undefined
  onSelectProduct?: () => void
  isMine?: boolean
}> = ({
  id,
  name,
  alias,
  imgUrl,
  price,
  inStock,
  currency,
  selectedProductId,
  onSelectProduct,
  isMine = false,
}) => {
  const isThreeStepIT = useIsThreeStepIT()
  const isSelected = selectedProductId === id

  return (
    <button
      className={twJoin(
        'relative flex w-[200px] flex-col items-center justify-around rounded-lg border-2 p-3 transition',
        isSelected &&
          (isThreeStepIT
            ? 'border-threeStepIT text-threeStepIT bg-white'
            : 'border-green-500 text-green-600'),
        !isSelected && 'border-transparent hover:shadow-md'
      )}
      onClick={onSelectProduct}
    >
      <div className="mb-2">
        <ImageWithIconFallback
          src={imgUrl}
          alt="product"
          className="mx-auto block max-h-36 min-h-40 max-w-full"
          fallbackIcon={
            <ImageOffOutlineIcon
              className={twJoin(
                'mx-auto h-24 w-24',
                isThreeStepIT ? 'text-threeStepIT-codex' : 'text-gray-400'
              )}
            />
          }
        />
      </div>

      <div className="mt-2 text-center">{alias || name}</div>

      {!isMine && (
        <div className="mt-2 text-center text-xl font-semibold">
          <FormattedNumberWithCurrency amount={price} currency={currency} />
        </div>
      )}

      {!isMine && (
        <div
          className={twJoin(
            'text-center',
            isThreeStepIT ? 'text-threeStepIT-codex' : 'text-gray-600'
          )}
        >
          {parseInStock(inStock || null)} <Translation id="in stock" />
        </div>
      )}

      {Boolean(onSelectProduct) && (
        <div className="absolute right-2 top-2">
          {isSelected ? (
            <CheckboxMarkedCircleOutlineIcon
              className={twJoin('h-6 w-6', isThreeStepIT ? 'text-threeStepIT' : 'text-green-500')}
            />
          ) : (
            <CheckboxBlankCircleOutlineIcon
              className={twJoin(
                'h-6 w-6',
                isThreeStepIT ? 'text-threeStepIT-codex' : 'text-gray-400'
              )}
            />
          )}
        </div>
      )}
    </button>
  )
}
